// Centralized API endpoint paths
// All request paths are defined here for easy maintenance and environment switching.

export const USER_ENDPOINTS = {
  profile: '/user/profile',
  tags: '/user/tag',
  tag: (tagId: string) => `/user/tag/${tagId}`,
  asset: '/user/asset',
  points: '/user/points',
  greeting: (platform: string, type: string) => `/user/greeting/${platform}/${type}`,
  candidateLink: (platform: string) => `/user/candidate_link/${platform}`,
  jobs: '/user/job',
  job: (jobId: string) => `/user/job/${jobId}`,
} as const

export const ONBOARDING_ENDPOINTS = {
  status: '/user/onboarding/status',
  refreshTask: (taskKey: string) => `/user/onboarding/tasks/${encodeURIComponent(taskKey)}/refresh`,
  claimTask: (taskKey: string) => `/user/onboarding/tasks/${encodeURIComponent(taskKey)}/claim`,
  recordEvent: (taskKey: string) => `/user/onboarding/tasks/${encodeURIComponent(taskKey)}/event`,
} as const

export const COMMON_ENDPOINTS = {
  language: '/enum/language',
  function: '/enum/function',
  schoolMajor: '/enum/school_major',
  eduLevel: '/enum/edu_level',
  location: '/enum/location',
  locationByCode: (code: string) => `/enum/location/${code}`,
} as const

export const PAY_ENDPOINTS = {
  products: '/pay/product',
  createOrder: '/pay/order',
  order: (orderId: string) => `/pay/order/${orderId}`,
  orders: '/pay/orders',
  subscription: '/pay/subscription',
  subscriptionCancel: (subscriptionId: string) => `/pay/subscription/${subscriptionId}/cancel`,
} as const

export const NEW_USER_GIFT_ENDPOINTS = {
  publicInfo: (giftCode: string) => `/new-user-gift/${encodeURIComponent(giftCode)}/public`,
  myStatus: (giftCode: string) => `/new-user-gift/${encodeURIComponent(giftCode)}/me`,
  claim: (giftCode: string) => `/new-user-gift/${encodeURIComponent(giftCode)}/claim`,
} as const

export const REFERRAL_ENDPOINTS = {
  ticket: (code: string) => `/referral/${encodeURIComponent(code)}/ticket`,
  accept: '/user/referral/accept',
  overview: '/user/referral/overview',
  invites: '/user/referral/invites',
  rewards: '/user/referral/rewards',
} as const

export const LOGTO_ENDPOINTS = {
  myAccount: '/api/my-account',
  myAccountIdentities: '/api/my-account/identities',
  myAccountIdentity: (connectorTargetId: string) =>
    `/api/my-account/identities/${connectorTargetId}`,
  verificationsSocial: '/api/verifications/social',
  verificationsSocialVerify: '/api/verifications/social/verify',
  verificationsPassword: '/api/verifications/password',
  verificationsPasswordVerify: '/api/verifications/password/verify',
  verificationsCode: '/api/verifications/verification-code',
  verificationsCodeVerify: '/api/verifications/verification-code/verify',
} as const

export const MESSAGE_ENDPOINTS = {
  // Email Config
  emailConfigs: '/message/email/config',
  emailConfig: (id: string) => `/message/email/config/${id}`,
  emailConfigSendTest: '/message/email/config/send-test',
  // Email Send
  emailSend: '/message/email/send',
  emailSendBatch: '/message/email/send/batch',
  emailLogs: '/message/email/logs',
  inboxAccounts: '/message/email/inbox/accounts',
  inboxAccount: (configId: string) => `/message/email/inbox/accounts/${configId}`,
  inboxSync: (configId: string) => `/message/email/inbox/accounts/${configId}/sync`,
  inboxFolders: '/message/email/inbox/folders',
  inboxThreads: '/message/email/inbox/threads',
  inboxThread: (threadId: string) => `/message/email/inbox/threads/${threadId}`,
  inboxThreadRead: (threadId: string) => `/message/email/inbox/threads/${threadId}/read`,
  inboxThreadUnread: (threadId: string) => `/message/email/inbox/threads/${threadId}/unread`,
  inboxThreadArchive: (threadId: string) => `/message/email/inbox/threads/${threadId}/archive`,
  inboxThreadUnarchive: (threadId: string) => `/message/email/inbox/threads/${threadId}/unarchive`,
  inboxThreadReply: (threadId: string) => `/message/email/inbox/threads/${threadId}/reply`,
  inboxThreadCandidate: (threadId: string) => `/message/email/inbox/threads/${threadId}/candidate`,
  inboxContactCandidates: (email: string) =>
    `/message/email/inbox/contacts/${encodeURIComponent(email)}/candidates`,
  inboxCandidateResume: (candidateId: string) =>
    `/message/email/inbox/candidates/${encodeURIComponent(candidateId)}/resume`,
  outreachCandidateResume: (campaignId: string) =>
    `/message/email/sequences/campaigns/${encodeURIComponent(campaignId)}/candidate-resume`,
  inboxSend: '/message/email/inbox/send',
  inboxAttachments: '/message/email/inbox/attachments',
  inboxAttachment: (id: string) => `/message/email/inbox/attachments/${id}`,
  inboxAccountReauthorize: (configId: string) =>
    `/message/email/inbox/accounts/${configId}/reauthorize`,
  // Email Sending Control
  sendingControl: '/message/email/sending-control',
  sendingControlConfig: (id: string) =>
    `/message/email/config/${encodeURIComponent(id)}/sending-control`,
  campaignSendEstimate: (id: string) =>
    `/message/email/sequences/campaigns/${encodeURIComponent(id)}/send-estimate`,
  // Email Templates
  templates: (type: string) => `/message/template/${type}`,
  template: (type: string, id: string) => `/message/template/${type}/${id}`,
  // Candidate Messages
  candidateMessages: (candidateId: string) => `/message/candidate/${candidateId}`,
  candidateEmails: (candidateId: string) => `/message/email/candidate/${candidateId}`,
  inmailGenerateByJob: (jobId: string) => `/message/inmail/generate/job/${jobId}`,
  inmailGenerateByTalentPool: '/message/inmail/generate/talent-pool',
  inmailTalentPoolPreflight: '/message/inmail/talent-pool/preflight',
} as const

export const CANDIDATE_ENDPOINTS = {
  importContacts: '/candidate/resume/import-contacts',
  tagCandidates: (tagId: string) => `/candidate/tag/${tagId}`,
  tagCandidate: (tagId: string, candidateId: string | number) =>
    `/candidate/tag/${tagId}/${candidateId}`,
  contact: (candidateId: string | number, contactType: string) =>
    `/candidate/contact/${candidateId}/${contactType}`,
  contactBulkEmail: '/candidate/internal/contact/bulk-email',
  apolloBulkEmail: '/candidate/internal/apollo/bulk-email',
  jobCandidates: (jobId: string) => `/candidate/jobs/${jobId}`,
  jobCandidatesReanalysis: (jobId: string) => `/candidate/jobs/${jobId}/reanalysis`,
  candidateJob: (candidateId: string | number, jobId: string) =>
    `/candidate/${candidateId}/jobs/${jobId}`,
  candidateJobNotes: (candidateId: string | number, jobId: string) =>
    `/candidate/${candidateId}/jobs/${jobId}/notes`,
  candidateJobNote: (candidateId: string | number, jobId: string, noteId: string) =>
    `/candidate/${candidateId}/jobs/${jobId}/notes/${noteId}`,
  candidateJobReport: (candidateId: string | number, jobId: string) =>
    `/candidate/${candidateId}/jobs/${jobId}/report`,
} as const
